/**
 * @file THE map renderer. ONE module, THREE consumers: `Minimap.jsx` (the 220 px
 * bottom-left window), `PauseMenu.jsx`'s full-screen MAP view, and
 * `MissionArrow.jsx` — which draws no map at all and takes only the ORIENTATION,
 * through {@link screenBearing}. Everything about how the island looks on a 2D
 * map lives here and nowhere else — if the minimap and the pause map ever
 * disagree, someone drew outside this file.
 *
 * ⚠ The third consumer is why {@link screenBearing} is exported from here rather
 * than living in the HUD component that uses it: "which way is up on this map"
 * has exactly one definition, and the failure mode of a second copy is a MIRROR,
 * which looks completely correct in a screenshot and is unusable in motion. This
 * project has already paid for that once — see THE SIGN below.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE CONTRACT IT READS — `level.map` (`LevelMapData` in `engine/types.js`)
 * ─────────────────────────────────────────────────────────────────────────────
 * ```
 * level.map = {
 *   seaLevel:   number,
 *   plateauY:   number,
 *   cityRect:   {x, z, w, d},                    // MIN corner + extents; the
 *                                                //   FIRST plateau only
 *   cityRects:  [{x, z, w, d, id}, ...],         // EVERY city's plateau
 *   coastlines: [ [{x, z}, ...], ... ],          // ONE ORDERED CLOSED RING PER
 *                                                //   ISLAND; first point NOT
 *                                                //   repeated at the end
 *   roads: {
 *     lattice: [ ...roads.edges ],               // paved rect + class/width/axis
 *     country: [{id, kind, width, points, head}],
 *   },
 *   districts: [{blockId, zone, rect}],          // zone: downtown|midrise|
 * }                                              //   residential|industrial|
 *                                                //   park|plaza, or null
 * ```
 * EVERY field is read defensively, because three things are true at once: dev
 * scenes have no `map` at all, the level can be mid-rebuild, and the shape is
 * shared with consumers written by other hands. Rect spellings are normalised
 * by {@link rectBounds} — `{minX,minZ,maxX,maxZ}`, the project's `{x, z, w, d}`
 * min-corner form, and `{x, z, w, h}` all work. When nothing drawable comes
 * back, {@link buildBase} returns `null` and the consumer draws a placeholder.
 *
 * ⚠ `coastlines` IS A LIST OF RINGS AND MUST NEVER BE FLATTENED. It replaced a
 * single `coastline` when the world became an ARCHIPELAGO (`engine/world/
 * Archipelago.js`). Every ring is closed independently, so concatenating two
 * islands' rings into one path draws a segment straight across the open sea
 * between them — a land bridge, silently, that looks like scenery rather than a
 * bug. {@link coastRings} is the ONE place in this file that knows the shape
 * (including the legacy single-`coastline` fallback), and {@link drawCoast} is
 * the one place that draws it: one `moveTo`/`lineTo`…/`closePath` and one
 * fill-or-stroke PER RING. `tests/minimap.test.mjs` section (i) asserts no drawn
 * segment ever spans the strait.
 *
 * Two more traps the contract spells out and this file honours: a ring has no
 * duplicate terminator (`closePath` supplies it), and a country route with
 * `kind: 'head'` is a cul-de-sac turning circle whose `points` hold ONLY the
 * centre — its geometry is in `head: {x, z, r}`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE STATIC BASE IS RENDERED ONCE
 * ─────────────────────────────────────────────────────────────────────────────
 * Ocean, landmass, districts and roads never move, so they are painted a single
 * time into an offscreen canvas at a fixed world scale ({@link buildBase}) and
 * every frame after that is one `drawImage` ({@link blitBase}). The base is
 * cached in a `WeakMap` keyed by the `map` OBJECT — so the minimap and the
 * pause map share one bitmap, a level reload gets a fresh one, and a disposed
 * level's base is collectable without anyone calling a dispose method.
 *
 * ⚠ The base canvas is capped at {@link BASE_MAX} px on its longest side, so on
 * a big enough world the cap — not {@link BASE_PX_PER_M} — is what decides the
 * resolution. `base.scale` is the number to trust, never the requested one; the
 * arithmetic for both world sizes is on `BASE_MAX` itself.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ORIENTATION — THE BITMAP IS NORTH-UP; ROTATION IS PER CONSUMER
 * ─────────────────────────────────────────────────────────────────────────────
 * World +X is map RIGHT, world +Z is map DOWN, so map "north" (up) is world -Z.
 * {@link buildBase} bakes that and nothing else: the base bitmap is north-up
 * FOREVER, and so are {@link viewX} / {@link viewY}, which is why they are
 * described below as MAP SPACE rather than screen space.
 *
 * A consumer that wants a rotating map sets {@link MapView}`.rot` and calls
 * {@link applyViewRotation} — the canvas transform then spins the whole map
 * plane about the canvas centre and every drawing call inside it keeps using
 * the same north-up coordinates. `rot` is ABSENT (0) by default, so the pause
 * menu's full map is north-up with no branch taken and nothing to regress. Only
 * `Minimap.jsx` sets it; see its header for the traps rotation opens.
 *
 * THE SIGN (`tests/minimap.test.mjs` section (a) pins it). The camera sits at
 * `target + (sin y, _, cos y) * d` (`Engine#updateCamera`), so it LOOKS along
 * `f = (-sin y, -cos y)`; in map space that vector is already a screen offset.
 * Canvas `rotate(t)` is `[c, s; -s, c]`, i.e. it ADDS `t` to a screen-space
 * `atan2(y, x)` — clockwise, because Y points down. Sending `f` to screen-up
 * `(0, -1)` therefore needs `t = -PI/2 - atan2(-cos y, -sin y)`, and that
 * whole expression collapses to `t = y`. {@link mapRotation} IS that identity,
 * kept as a named function because it is a derivation and not a coincidence.
 *
 * A heading (`yaw`) uses the engine's convention — `Math.atan2(dx, dz)`, i.e.
 * 0 points along +Z and the angle grows toward +X (see `EnemyManager#yawOf`).
 * On the map that is 0 = DOWN, which is why {@link drawPlayerMarker} rotates by
 * `PI - yaw`: a chevron authored pointing up must land pointing along
 * `(sin yaw, cos yaw)` in canvas space. Drawn INSIDE a rotated context the two
 * angles compose to `rot + PI - yaw`, which is the heading relative to the
 * camera — exactly what the marker should read as, and free.
 *
 * @module ui/hud/mapRender
 */

/**
 * Longest side of the offscreen base canvas, px. It caps map memory — and on an
 * ARCHIPELAGO-sized world it is also what decides whether the map is readable,
 * so the number is derived, not picked.
 *
 * The base is painted at `min(BASE_PX_PER_M, BASE_MAX / extent)` px/m, where the
 * extent is the world span plus {@link mapExtent}'s 7 % margin on EACH side
 * (× 1.14). That gives, for the two world sizes this project ships:
 *
 * ```
 *   world span   extent    px/m at 2048    px/m at 4096
 *   ----------   ------    ------------    ------------
 *      720 m      821 m      2.50            3.00  (the cap stops binding)
 *     2000 m     2280 m      0.90            1.80
 * ```
 *
 * 0.90 px/m is the number that forced the raise. The minimap draws at 1.4 px/m,
 * so a 0.90 px/m base is UPSCALED 1.6× on the one widget the map is actually
 * read from — every road edge goes soft — and {@link drawDistricts}' hairline
 * gate (`scale > 1.5`) switches off, which is the block outline the street grid
 * is read by. At 1.80 px/m the base is still a slight DOWNSCALE on the minimap,
 * the hairlines stay on, and the road hierarchy survives in the bitmap the pause
 * map then shrinks: a 9 m street is 16 px against a 22 m boulevard's 40 px, both
 * far above {@link ROAD_MIN_PX}.
 *
 * 4096 AND NOT MORE BECAUSE THE BITMAP IS THE MEMORY. 4096² RGBA is 67 MB, and
 * it stays resident for the whole life of the level — it is a `WeakMap` value
 * keyed by `level.map`, so nothing evicts it early. 8192 would be 268 MB for one
 * HUD widget, which is not a trade this project makes for a sharper minimap.
 */
export const BASE_MAX = 4096;
/**
 * Requested base resolution, px per world metre (before the cap). At 720 m this
 * is now the binding number — see {@link BASE_MAX}'s table — so raising it costs
 * memory on the small world immediately, where before the cap absorbed it.
 */
export const BASE_PX_PER_M = 3;

/**
 * Floor on a road's drawn thickness in the base bitmap, px. Under a pixel a
 * road stops reading as a thin road: a filled rect antialiases into a grey
 * smear and a stroke into a dotted ghost, and the grid dissolves into a wash.
 *
 * It is a GUARD, not a tuning knob. It binds only below `1 / widthMetres` px/m
 * — 0.11 px/m for a 9 m street — and {@link BASE_MAX} holds the base at
 * 1.80 px/m even on the 2000 m world, so nothing this project ships comes near
 * it. It exists so that "the world got bigger" can never silently erase the
 * street grid, and it is applied to the LATTICE rects as well as the country
 * polylines — the rects had no floor at all, which is the asymmetry that would
 * have bitten first.
 */
const ROAD_MIN_PX = 1;

// ─────────────────────────────────────────────────────────────────────────────
// Palette — GTA2-flavoured: near-black water, cold slate land, pale roads.
// ─────────────────────────────────────────────────────────────────────────────

export const MAP_COLORS = Object.freeze({
  ocean: '#07131f',
  oceanBand: '#0b1e30',
  land: '#161b22',
  coast: '#33566e',
  block: '#101419',
  blockEdge: '#1b212a',
  grid: '#141821',
  rim: '#2a303c',
  label: '#7f8ba1',
  // THE SOUTH-WEST VILLAGES. Warm sand against the map's cold slate, because a
  // village is the one settlement on this island that is not a city block — the
  // same reasoning that makes a `dirt` road warm rather than thinner (see the
  // road table above). `villageEdge` is the ring, `villageLabel` the name.
  villageEdge: '#8a7551',
  villageLabel: '#b39668',
  north: '#e6e9f2',
  player: '#ffffff',
  playerEdge: '#0b0e14',
});

/** Blip colours. `kind` comes straight off the `'hud:blips'` payload. */
export const BLIP_COLORS = Object.freeze({
  police: '#3b82f6',
  gang_red: '#ef4444',
  gang_green: '#22c55e',
  // THE BLUE (ASIAN) CREW, whose def-id prefix still reads `gang_purple` — the
  // re-skin moved everything the player sees and left the id alone on purpose
  // (`game/factions.js`, header). CYAN, not the police's `#3b82f6`: the two are
  // the only cool colours on the map and a crew that could be mistaken for the
  // law at 3 px would make the minimap lie about who is chasing you.
  gang_purple: '#22d3ee',
  civilian: '#9ca3af',
  car: '#d1d5db',
  // THE RESUPPLY DRONE. Amber, and it is the only warm-yellow on the map: the
  // other six are a blue, three saturated primaries and two greys, so a 3 px
  // amber dot cannot be mistaken for anybody at a glance. It matches the
  // drone's own slung crate and its ground ring — one colour for one machine,
  // in the air, on the tarmac and on the map.
  //
  // ⚠ It is a BLIP and not a MARKER on purpose, by the rule stated above
  // {@link MARKER_COLORS}: a blip is somebody who is THERE and a marker is
  // somewhere you have to BE. The drone is coming to you; you never walk to it.
  drone: '#f59e0b',
});

/** Legend order + wording for the pause map. */
export const BLIP_LEGEND = Object.freeze([
  { kind: 'police', label: 'Police' },
  { kind: 'gang_red', label: 'Red crew' },
  { kind: 'gang_green', label: 'Green crew' },
  { kind: 'gang_purple', label: 'Blue crew' },
  { kind: 'civilian', label: 'Civilian' },
  { kind: 'car', label: 'Vehicle' },
]);

/**
 * MISSION MARKER colours, keyed by the `kind` on `engine/systems/Missions.js`'s
 * marker records. They deliberately do NOT overlap {@link BLIP_COLORS}: a blip
 * is somebody who is there, a marker is somewhere you have to be, and the two
 * must never be confused at a 3 px glance.
 */
export const MARKER_COLORS = Object.freeze({
  /** A story mission's pickup — the HUD's gold. */
  giver: '#e8b643',
  /** An optional one — cool steel. */
  giver_optional: '#7fb2d9',
  /** The live objective — white, the loudest thing on the map. */
  objective: '#ffffff',
});

/** Faint per-zone wash laid over the block fill. */
export const ZONE_TINT = Object.freeze({
  downtown: 'rgba(104,146,214,0.20)',
  midrise: 'rgba(126,150,190,0.13)',
  residential: 'rgba(196,170,120,0.10)',
  industrial: 'rgba(198,124,66,0.12)',
  park: 'rgba(64,182,104,0.20)',
  plaza: 'rgba(214,196,132,0.14)',
});

/** Display wording for a district label on the full map. */
export const ZONE_LABELS = Object.freeze({
  downtown: 'DOWNTOWN',
  midrise: 'MIDRISE',
  residential: 'RESIDENTIAL',
  industrial: 'INDUSTRIAL',
  park: 'PARK',
  plaza: 'PLAZA',
});

// ─────────────────────────────────────────────────────────────────────────────
// Geometry helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalises any of the three rect spellings this codebase uses into world-space
 * min/max. Writes into `out` so the hot paths never allocate.
 *
 * @param {any} r
 * @param {{minX:number,minZ:number,maxX:number,maxZ:number}} out
 * @returns {boolean} False when `r` is not a usable rect (caller skips it).
 */
export function rectBounds(r, out) {
  if (!r) return false;
  if (Number.isFinite(r.minX) && Number.isFinite(r.maxX) && Number.isFinite(r.minZ) && Number.isFinite(r.maxZ)) {
    out.minX = Math.min(r.minX, r.maxX);
    out.maxX = Math.max(r.minX, r.maxX);
    out.minZ = Math.min(r.minZ, r.maxZ);
    out.maxZ = Math.max(r.minZ, r.maxZ);
    return true;
  }
  // City form: min corner + extents. `d` is the Z extent, `h` the alias some
  // callers use — accept both rather than making the level agree with us.
  const w = Number.isFinite(r.w) ? r.w : Number.isFinite(r.width) ? r.width : NaN;
  const d = Number.isFinite(r.d) ? r.d : Number.isFinite(r.h) ? r.h : NaN;
  if (!Number.isFinite(r.x) || !Number.isFinite(r.z) || !Number.isFinite(w) || !Number.isFinite(d)) return false;
  out.minX = r.x;
  out.maxX = r.x + w;
  out.minZ = r.z;
  out.maxZ = r.z + d;
  return true;
}

/** Shared empty result, so the no-map path allocates nothing. */
const NONE = Object.freeze([]);

/**
 * THE ONE PLACE IN THIS FILE THAT KNOWS THE COASTLINE SHAPE — including the
 * legacy one. Returns the map's islands as a list of rings, each ring ordered,
 * closed-by-convention (no duplicate terminator) and stripped of any point that
 * is not finite.
 *
 * ⚠ The list is what the archipelago made of a single ring, and the rings must
 * stay SEPARATE all the way to the canvas. Anything that walks a ring closes it,
 * so a flattened list fabricates a segment from the last point of one island to
 * the first point of the next: a land bridge across open sea, drawn without an
 * error and indistinguishable from level design. Iterate this; never `flat()`.
 *
 * A level published before the archipelago carries a single `coastline` instead,
 * and is wrapped into a one-entry list here so that neither {@link mapExtent}
 * nor {@link drawCoast} has to carry a second shape. If a map somehow has both,
 * `coastlines` wins — it is the newer field and the one with every island in it.
 *
 * @param {any} map `level.map`, or null/undefined.
 * @returns {Array<Array<{x:number,z:number}>>} Never null; may be empty.
 */
export function coastRings(map) {
  const raw = Array.isArray(map?.coastlines)
    ? map.coastlines
    : Array.isArray(map?.coastline)
      ? [map.coastline]
      : null;
  if (!raw) return /** @type {any} */ (NONE);
  /** @type {Array<Array<{x:number,z:number}>>} */
  const out = [];
  for (const ring of raw) {
    if (!Array.isArray(ring)) continue;
    const clean = ring.filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.z));
    if (clean.length) out.push(clean);
  }
  return out;
}

/**
 * Every paved city plateau, as raw rects for {@link rectBounds} to normalise.
 * The archipelago twin of {@link coastRings}: `cityRects` holds one entry per
 * city and `cityRect` survives as the first of them, so a pre-archipelago map
 * still answers. Unlike the rings these need no cleaning — `rectBounds` already
 * rejects anything that is not a usable rect.
 *
 * @param {any} map
 * @returns {Array<any>} Never null; may be empty.
 */
export function cityPlateaus(map) {
  if (Array.isArray(map?.cityRects) && map.cityRects.length) return map.cityRects;
  return map?.cityRect ? [map.cityRect] : /** @type {any} */ (NONE);
}

/**
 * World-space extent the base bitmap covers: everything the map knows about,
 * plus a margin of open water so the island never touches the bitmap edge.
 *
 * @param {any} map
 * @param {number} [padFrac=0.07] Margin as a fraction of the larger side.
 * @returns {{minX:number,minZ:number,maxX:number,maxZ:number}|null}
 */
export function mapExtent(map, padFrac = 0.07) {
  if (!map) return null;
  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;
  const r = { minX: 0, minZ: 0, maxX: 0, maxZ: 0 };

  const swallow = (rect) => {
    if (!rectBounds(rect, r)) return;
    if (r.minX < minX) minX = r.minX;
    if (r.minZ < minZ) minZ = r.minZ;
    if (r.maxX > maxX) maxX = r.maxX;
    if (r.maxZ > maxZ) maxZ = r.maxZ;
  };

  // EVERY plateau, not just the first — with two cities, anchoring on
  // `cityRect` alone would leave the second one off the edge of the bitmap on
  // any map whose coastline had not been published yet.
  for (const rect of cityPlateaus(map)) swallow(rect);
  swallow(map.bounds);
  // Every island's ring. The extent does not care that the rings are separate,
  // but it must swallow ALL of them or half the archipelago falls off the base.
  for (const ring of coastRings(map)) {
    for (const p of ring) {
      if (p.x < minX) minX = p.x;
      if (p.z < minZ) minZ = p.z;
      if (p.x > maxX) maxX = p.x;
      if (p.z > maxZ) maxZ = p.z;
    }
  }
  for (const rd of map.roads?.lattice ?? []) swallow(rd);
  for (const road of map.roads?.country ?? []) {
    for (const p of road?.points ?? []) {
      if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.z)) continue;
      if (p.x < minX) minX = p.x;
      if (p.z < minZ) minZ = p.z;
      if (p.x > maxX) maxX = p.x;
      if (p.z > maxZ) maxZ = p.z;
    }
  }
  for (const d of map.districts ?? []) swallow(d?.rect ?? d);

  if (!Number.isFinite(minX) || !Number.isFinite(minZ) || maxX <= minX || maxZ <= minZ) return null;

  const pad = Math.max(maxX - minX, maxZ - minZ) * padFrac;
  return { minX: minX - pad, minZ: minZ - pad, maxX: maxX + pad, maxZ: maxZ + pad };
}

// ─────────────────────────────────────────────────────────────────────────────
// The static base bitmap
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} MapBase
 * @property {HTMLCanvasElement} canvas The painted bitmap.
 * @property {number} scale ACTUAL px per world metre (post-cap — trust this).
 * @property {number} minX @property {number} minZ World coord of pixel (0,0).
 * @property {number} maxX @property {number} maxZ
 * @property {number} w @property {number} h Bitmap size in px.
 * @property {number} worldW @property {number} worldH Extent in metres.
 */

/** @type {WeakMap<object, MapBase|null>} One bitmap per `level.map` object. */
const baseCache = new WeakMap();

/**
 * The cached base for a level's map, building it on first use. Returns `null`
 * (and caches the null) when the map carries nothing drawable — that is the
 * signal for a consumer to draw {@link drawPlaceholder} instead.
 *
 * @param {any} map `level.map`, or null/undefined.
 * @returns {MapBase|null}
 */
export function getBase(map) {
  if (!map || typeof map !== 'object') return null;
  if (baseCache.has(map)) return baseCache.get(map) ?? null;
  let base = null;
  try {
    base = buildBase(map);
  } catch (err) {
    console.warn('[mapRender] base build failed', err);
    base = null;
  }
  baseCache.set(map, base);
  return base;
}

/**
 * Paints ocean → landmass → districts → roads into a fresh offscreen canvas.
 * Callers normally want {@link getBase} (cached); this is exported so a future
 * "regenerate the map" debug affordance has something to call.
 *
 * @param {any} map
 * @param {{pxPerM?: number, maxSize?: number}} [opts]
 * @returns {MapBase|null}
 */
export function buildBase(map, opts = {}) {
  const ext = mapExtent(map);
  if (!ext || typeof document === 'undefined') return null;

  const worldW = ext.maxX - ext.minX;
  const worldH = ext.maxZ - ext.minZ;
  const maxSize = opts.maxSize ?? BASE_MAX;
  const want = opts.pxPerM ?? BASE_PX_PER_M;
  const scale = Math.min(want, maxSize / Math.max(worldW, worldH));
  const w = Math.max(2, Math.round(worldW * scale));
  const h = Math.max(2, Math.round(worldH * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  /** @type {MapBase} */
  const base = { canvas, scale, minX: ext.minX, minZ: ext.minZ, maxX: ext.maxX, maxZ: ext.maxZ, w, h, worldW, worldH };
  const X = (wx) => (wx - ext.minX) * scale;
  const Y = (wz) => (wz - ext.minZ) * scale;

  // 1. open water.
  ctx.fillStyle = MAP_COLORS.ocean;
  ctx.fillRect(0, 0, w, h);

  // 2. landmass. Closed coastlines are the real thing; without them, the city
  //    plateaus stand in so a half-built map still reads as land-with-roads.
  ctx.save();
  if (!drawCoast(ctx, coastRings(map), X, Y, scale)) {
    ctx.fillStyle = MAP_COLORS.land;
    const r = { minX: 0, minZ: 0, maxX: 0, maxZ: 0 };
    let any = false;
    for (const rect of cityPlateaus(map)) {
      if (!rectBounds(rect, r)) continue;
      ctx.fillRect(X(r.minX), Y(r.minZ), (r.maxX - r.minX) * scale, (r.maxZ - r.minZ) * scale);
      any = true;
    }
    // Nothing at all to go on: the whole bitmap is land, which at least puts
    // the roads on something. No clip either way — there is no shore to clip to.
    if (!any) ctx.fillRect(0, 0, w, h);
  }

  // 3. districts: a darker block slab, then the zone wash on top.
  drawDistricts(ctx, map, X, Y, scale);

  // 4. roads, over the blocks (a map is read by its road grid).
  drawRoads(ctx, map, X, Y, scale);

  ctx.restore();
  return base;
}

/**
 * Adds ONE ring to the current path as its own closed subpath. The ring carries
 * no duplicate terminator (the contract), so `closePath` is what shuts it —
 * which is also why the ring boundary is a real boundary and not a suggestion.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<{x:number,z:number}>} ring
 * @param {(x:number)=>number} X @param {(z:number)=>number} Y
 */
function ringSubpath(ctx, ring, X, Y) {
  ctx.moveTo(X(ring[0].x), Y(ring[0].z));
  for (let i = 1; i < ring.length; i++) ctx.lineTo(X(ring[i].x), Y(ring[i].z));
  ctx.closePath();
}

/**
 * THE LANDMASS: shallow-water band, land fill, shoreline — then the CLIP that
 * puts every later layer (districts, roads) on land.
 *
 * ⚠ ONE PATH PER RING for everything that paints. An archipelago's rings are
 * separate islands, and a single path walked across all of them would join the
 * last point of one to the first of the next: a land bridge across open sea,
 * filled and shored like real coastline, with no error anywhere. Each ring
 * therefore gets its own `beginPath`/`closePath` and its own fill or stroke.
 * `tests/minimap.test.mjs` section (i) asserts no drawn segment spans the
 * strait, and proves the assertion can see a deliberately flattened ring.
 *
 * ⚠ THE CLIP IS THE ONE EXCEPTION, AND IT MUST BE. `ctx.clip()` INTERSECTS with
 * the clip already installed, so clipping to island A and then to island B
 * leaves the EMPTY SET and both cities vanish. The clip is a single path holding
 * every ring as a SUBPATH — still one `moveTo`…`closePath` each, still no
 * segment between islands, but one `clip()` call for the union.
 *
 * ⚠ THE THREE PASSES ARE ORDERED, not merged into one loop. The band is drawn
 * 9 m WIDE and centred on the shore, so half of it lies over water outside the
 * island; across a narrow strait that half falls on the NEIGHBOUR. Band-then-
 * fill-then-shore per island would let the second island's band paint over the
 * first island's land. All bands, then all fills, then all shorelines. With one
 * island the three passes collapse to exactly the sequence this file has always
 * drawn, byte for byte.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<Array<{x:number,z:number}>>} rings From {@link coastRings}.
 * @param {(x:number)=>number} X @param {(z:number)=>number} Y
 * @param {number} scale
 * @returns {boolean} False when there was no drawable land — the caller falls
 *   back to the city plateaus and installs no clip.
 */
function drawCoast(ctx, rings, X, Y, scale) {
  // Two points are a line, not an island. Filtering here rather than in
  // `coastRings` keeps the "what is drawable" decision with the drawing.
  const islands = rings.filter((r) => r.length >= 3);
  if (!islands.length) return false;

  ctx.lineJoin = 'round';

  // Pass 1 — shallow-water band just outside each shore. One stroke, no blur;
  // reads as a coastal shelf at both zoom levels.
  ctx.strokeStyle = MAP_COLORS.oceanBand;
  ctx.lineWidth = Math.max(2, 9 * scale);
  for (const ring of islands) {
    ctx.beginPath();
    ringSubpath(ctx, ring, X, Y);
    ctx.stroke();
  }

  // Pass 2 — land, over the inner half of every band.
  ctx.fillStyle = MAP_COLORS.land;
  for (const ring of islands) {
    ctx.beginPath();
    ringSubpath(ctx, ring, X, Y);
    ctx.fill();
  }

  // Pass 3 — the shoreline hairline on top.
  ctx.strokeStyle = MAP_COLORS.coast;
  ctx.lineWidth = Math.max(1, 1.2 * scale);
  for (const ring of islands) {
    ctx.beginPath();
    ringSubpath(ctx, ring, X, Y);
    ctx.stroke();
  }

  // Everything after this is CITY, and every city is on an island.
  ctx.beginPath();
  for (const ring of islands) ringSubpath(ctx, ring, X, Y);
  ctx.clip();
  return true;
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {any} map
 * @param {(x:number)=>number} X @param {(z:number)=>number} Y
 * @param {number} scale
 */
function drawDistricts(ctx, map, X, Y, scale) {
  const districts = map.districts;
  if (!Array.isArray(districts) || !districts.length) return;
  const r = { minX: 0, minZ: 0, maxX: 0, maxZ: 0 };
  const hair = scale > 1.5;

  for (const d of districts) {
    if (!rectBounds(d?.rect ?? d, r)) continue;
    const x = X(r.minX);
    const y = Y(r.minZ);
    const rw = (r.maxX - r.minX) * scale;
    const rh = (r.maxZ - r.minZ) * scale;
    if (!(rw > 0.5) || !(rh > 0.5)) continue;
    ctx.fillStyle = MAP_COLORS.block;
    ctx.fillRect(x, y, rw, rh);
    const tint = ZONE_TINT[d?.zone];
    if (tint) {
      ctx.fillStyle = tint;
      ctx.fillRect(x, y, rw, rh);
    }
    if (hair) {
      ctx.strokeStyle = MAP_COLORS.blockEdge;
      ctx.lineWidth = 1;
      ctx.strokeRect(x + 0.5, y + 0.5, Math.max(1, rw - 1), Math.max(1, rh - 1));
    }
  }
}

/**
 * Lattice rects first, country polylines over them. The lattice rects EXCLUDE
 * their intersection patches (see `levels/city/roads.js#makeEdge`), so each one
 * is extended by half a road width at both ends along its long axis — without
 * that the grid draws as dashes with a hole at every junction.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {any} map
 * @param {(x:number)=>number} X @param {(z:number)=>number} Y
 * @param {number} scale
 */
function drawRoads(ctx, map, X, Y, scale) {
  const roads = map.roads ?? {};
  const r = { minX: 0, minZ: 0, maxX: 0, maxZ: 0 };

  for (const rd of roads.lattice ?? []) {
    if (!rectBounds(rd, r)) continue;
    let x0 = r.minX;
    let z0 = r.minZ;
    let x1 = r.maxX;
    let z1 = r.maxZ;
    const across = Math.min(x1 - x0, z1 - z0);
    const grow = across * 0.5;
    // `axis: 'x'` means the line's position is measured on X, so the segment
    // RUNS along Z. Fall back to the rect's own aspect when no axis is given.
    const alongZ = rd.axis === 'x' ? true : rd.axis === 'z' ? false : z1 - z0 >= x1 - x0;
    if (alongZ) {
      z0 -= grow;
      z1 += grow;
    } else {
      x0 -= grow;
      x1 += grow;
    }

    // {@link ROAD_MIN_PX} on the ACROSS axis — the road's width, which after the
    // grow above is unambiguously the short side. The floor is re-centred so it
    // can only make a road thicker, never move it: a grid that shifted half a
    // pixel per road would stop lining up with the blocks under it. Inert at
    // every scale this file runs at (see `ROAD_MIN_PX`), so today's bitmap is
    // unchanged.
    let px = X(x0);
    let py = Y(z0);
    let pw = (x1 - x0) * scale;
    let ph = (z1 - z0) * scale;
    if (alongZ) {
      if (pw < ROAD_MIN_PX) {
        px -= (ROAD_MIN_PX - pw) * 0.5;
        pw = ROAD_MIN_PX;
      }
    } else if (ph < ROAD_MIN_PX) {
      py -= (ROAD_MIN_PX - ph) * 0.5;
      ph = ROAD_MIN_PX;
    }
    ctx.fillStyle = roadColor(rd.class);
    ctx.fillRect(px, py, pw, ph);
  }

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const road of roads.country ?? []) {
    const colour = roadColor(road?.class ?? road?.kind ?? 'country');
    const width = Number.isFinite(road?.width) ? road.width : 7;

    // A `kind: 'head'` entry is a cul-de-sac turning circle: its `points` hold
    // only the centre and the geometry lives in `head` (see `LevelMapData`).
    // Drawing it as a disc is the difference between a country lane that ends
    // in a bulb and one that just stops in a field.
    const head = road?.head;
    if (head && Number.isFinite(head.x) && Number.isFinite(head.z)) {
      ctx.fillStyle = colour;
      ctx.beginPath();
      ctx.arc(X(head.x), Y(head.z), Math.max(1, (Number.isFinite(head.r) ? head.r : width) * scale), 0, Math.PI * 2);
      ctx.fill();
    }

    const pts = road?.points;
    if (!Array.isArray(pts) || pts.length < 2) continue;
    ctx.strokeStyle = colour;
    ctx.lineWidth = Math.max(ROAD_MIN_PX, width * scale);
    ctx.beginPath();
    ctx.moveTo(X(pts[0].x), Y(pts[0].z));
    for (let i = 1; i < pts.length; i++) ctx.lineTo(X(pts[i].x), Y(pts[i].z));
    ctx.stroke();
  }
}

/**
 * @param {string|undefined} cls
 * @returns {string}
 */
function roadColor(cls) {
  switch (cls) {
    case 'boulevard':
      return '#525b6c';
    case 'avenue':
      return '#454d5c';
    case 'street':
      return '#3a4250';
    // The ALLEY — the bottom of the hierarchy, and the one road on the map that
    // is SUPPOSED to disappear. It is 4.5 m wide, so at minimap scale it is
    // already at `ROAD_MIN_PX` and painting it at street value would make a
    // pedestrian cut read as thick as the road it opens off. A step darker than
    // a street, i.e. barely above the land under it, so it shows as a hairline
    // when you are on top of it and melts into the block when you zoom out.
    case 'alley':
      return '#333a46';
    case 'country':
      return '#3f4655';
    // ── the out-of-town TIERS ────────────────────────────────────────────────
    // A country route arrives here by its `kind` (it publishes no `class`), so
    // these three are the tier names from `levels/city/config.js#country.tiers`
    // and the ordinary radial / loop / link kinds keep falling through to the
    // default. The VALUES are a hierarchy, not decoration: the whole reason for
    // a tier is that you can tell at a glance which way out of town is the fast
    // one, and the minimap is where that glance happens. The stroke width
    // already scales with `width`, so colour only has to reinforce the order —
    // the highway a step brighter than the boulevard that is the brightest
    // thing in the city, the trunk between the two, and the dirt track WARM and
    // dark so it reads as a different KIND of road rather than a thinner one.
    case 'highway':
      return '#7c88a0';
    case 'trunk':
      return '#5a6478';
    case 'dirt':
      return '#5d4f3d';
    default:
      return '#414957';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Views: world <-> canvas
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A camera onto the map. Plain data so consumers can keep ONE and mutate it
 * every frame — nothing here allocates per frame.
 * @typedef {Object} MapView
 * @property {number} cx @property {number} cz World point at the canvas centre.
 * @property {number} scale Canvas px (CSS px) per world metre.
 * @property {number} w @property {number} h Canvas size in CSS px.
 * @property {number} [rot=0] Canvas rotation about the centre, radians — see
 *   {@link mapRotation}. ABSENT means north-up, which is what the full map is
 *   and always will be. Only {@link applyViewRotation}, {@link viewPoint} and
 *   {@link inViewWindow} read it; {@link viewX}/{@link viewY} never do.
 */

/**
 * MAP SPACE: north-up canvas px. This is the space every drawing call uses,
 * rotated or not — under rotation the caller has already spun the context, so
 * a blip drawn at `viewX/viewY` lands in the right place with no extra maths.
 * @param {MapView} v @param {number} worldX @returns {number}
 */
export function viewX(v, worldX) {
  return v.w * 0.5 + (worldX - v.cx) * v.scale;
}

/** @param {MapView} v @param {number} worldZ @returns {number} */
export function viewY(v, worldZ) {
  return v.h * 0.5 + (worldZ - v.cz) * v.scale;
}

// ─────────────────────────────────────────────────────────────────────────────
// Rotation. Opt-in per consumer — see the ORIENTATION note in the file header.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * How much bigger a full-window fill must be to survive the rotation. A square
 * spun about its centre sweeps its own circumcircle, so anything that has to
 * cover the window from INSIDE a rotated context (the placeholder grid) is
 * drawn over `size * ROT_OVERSCAN`, centred. Fills that can be done OUTSIDE the
 * rotation (the ocean backdrop) should be — they cost nothing and cannot gap.
 */
export const ROT_OVERSCAN = Math.SQRT2;

/**
 * The canvas rotation that puts the CAMERA's forward at screen-up, i.e. makes
 * minimap-up equal screen-up. The derivation — and why it collapses to the
 * identity — is in the file header; `tests/minimap.test.mjs` pins the sign
 * against the real camera convention so a future camera change cannot silently
 * mirror the map.
 *
 * @param {number} camYaw `engine.yaw`, radians. Free-running (`CameraRig` never
 *   normalises it), which is fine: canvas `rotate` takes any magnitude.
 * @returns {number} Radians, clockwise-positive in screen space.
 */
export function mapRotation(camYaw) {
  return Number.isFinite(camYaw) ? camYaw : 0;
}

/**
 * Installs `view.rot` on the context as a rotation about the canvas CENTRE —
 * which, on the minimap, is the player, so the world turns about them.
 *
 * @param {CanvasRenderingContext2D} ctx Caller owns the save/restore.
 * @param {MapView} v
 * @returns {boolean} False when nothing was applied (`rot` absent or 0). A
 *   caller can use that to skip the overscan work a rotated fill needs.
 */
export function applyViewRotation(ctx, v) {
  const rot = Number.isFinite(v?.rot) ? v.rot : 0;
  if (!rot) return false;
  const cx = v.w * 0.5;
  const cy = v.h * 0.5;
  ctx.translate(cx, cy);
  ctx.rotate(rot);
  ctx.translate(-cx, -cy);
  return true;
}

/**
 * World point → FINAL screen px, honouring `view.rot`: the analytic twin of
 * `viewX/viewY` + {@link applyViewRotation}. Nothing in the draw path needs it,
 * because the context transform does the work — it exists so the rotation can
 * be PROVEN without a canvas (`tests/minimap.test.mjs` measures the sign and
 * the cull through it), and it is what a future off-screen edge-arrow or a
 * click-to-waypoint hit test would ask for.
 *
 * At `rot` 0 it returns `viewX`/`viewY` unchanged, bit for bit.
 *
 * @param {MapView} v
 * @param {number} worldX @param {number} worldZ
 * @param {{x:number,y:number}} out Reused.
 * @returns {{x:number,y:number}} `out`.
 */
export function viewPoint(v, worldX, worldZ, out) {
  const px = viewX(v, worldX);
  const py = viewY(v, worldZ);
  const rot = Number.isFinite(v?.rot) ? v.rot : 0;
  if (!rot) {
    out.x = px;
    out.y = py;
    return out;
  }
  const cx = v.w * 0.5;
  const cy = v.h * 0.5;
  const dx = px - cx;
  const dy = py - cy;
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  // Canvas `rotate` is [c, s; -s, c] — Y-down, so positive reads clockwise.
  out.x = cx + dx * c - dy * s;
  out.y = cy + dx * s + dy * c;
  return out;
}

/**
 * Can a MAP-SPACE point possibly be inside the window? The off-screen reject
 * for blips, and the only place the two orientations differ:
 *
 *  • north-up — the window is the axis-aligned box, so the box test is EXACT
 *    and this is byte-for-byte the test the minimap has always run;
 *  • rotated — the box test is no longer exact, and tightening it per point
 *    would cost a sin/cos each. Rotation preserves DISTANCE from the centre, so
 *    the half-diagonal is a conservative bound: it can never reject something
 *    visible, and the handful of corner-slop blips it lets through are clipped
 *    by the rounded rect for free.
 *
 * @param {MapView} v
 * @param {number} px @param {number} py Map space (`viewX`/`viewY`).
 * @param {number} [pad=0] Glyph radius, so a blip half off the edge still draws.
 * @returns {boolean}
 */
export function inViewWindow(v, px, py, pad = 0) {
  const rot = Number.isFinite(v?.rot) ? v.rot : 0;
  if (!rot) return px >= -pad && py >= -pad && px <= v.w + pad && py <= v.h + pad;
  const dx = px - v.w * 0.5;
  const dy = py - v.h * 0.5;
  const r = Math.sqrt(v.w * v.w + v.h * v.h) * 0.5 + pad;
  return dx * dx + dy * dy <= r * r;
}

/**
 * Where NORTH (world -Z) points once the map has been spun by `rot`, as a unit
 * screen direction. Chrome is drawn OUTSIDE the rotated context — a bezel that
 * rotates is a broken HUD, and a letter that rotates is unreadable — so the
 * north badge orbits the rim on this vector instead of being carried by the
 * transform.
 *
 * @param {number} rot Radians.
 * @param {{x:number,y:number}} [out] Reused; omit only off the hot path.
 * @returns {{x:number,y:number}} `out`.
 */
export function northScreenDir(rot, out = { x: 0, y: 0 }) {
  const r = Number.isFinite(rot) ? rot : 0;
  // (0,-1) put through the same [c, s; -s, c].
  out.x = Math.sin(r);
  out.y = -Math.cos(r);
  return out;
}

/**
 * WHICH WAY ON SCREEN a world offset points, as an angle you can hand straight
 * to a CSS/SVG `rotate()`: **0 is screen-UP, positive is CLOCKWISE.**
 *
 * This is the minimap's transform with the map thrown away. `viewX`/`viewY` put
 * world +X at map-right and world +Z at map-DOWN, `applyViewRotation` spins that
 * by `mapRotation(camYaw)` through the canvas matrix `[c, s; -s, c]`, and screen
 * up is `-y` — so the whole chain collapses to one `atan2` with no view, no
 * scale and no canvas. It lives HERE rather than in its one consumer for the
 * reason the whole file exists: there is exactly ONE definition of "which way is
 * up on this map", and a second copy of it in a HUD component is a mirror
 * waiting to happen. `tests/minimap.test.mjs` (k) proves it agrees with
 * {@link viewPoint} at arbitrary yaws, so the two can never drift.
 *
 * ⚠ THE SCALE IS DELIBERATELY ABSENT AND SO IS THE DISTANCE. A bearing is a
 * direction; anything that also wants "how far" measures it in WORLD metres,
 * where it means something, rather than reading it off a map at some px/m.
 *
 * @param {number} camYaw `engine.yaw`, radians. Free-running is fine.
 * @param {number} dx World X, TARGET minus viewer.
 * @param {number} dz World Z, TARGET minus viewer.
 * @returns {number} Radians clockwise from screen-up, in `(-PI, PI]`. A zero or
 *   non-finite offset returns 0, which is the only answer a caller could use.
 */
export function screenBearing(camYaw, dx, dz) {
  // ⚠ THE ZERO GUARD IS NOT DEFENSIVE PADDING. Screen-up is `-y`, and negating
  // a positive zero gives `-0`, so `atan2(0, -0)` is PI — a viewer standing
  // exactly on their target would be told to turn round. Callers hide the arrow
  // long before that, but "the degenerate case answers 0" is cheaper to
  // guarantee than to rely on.
  if (!Number.isFinite(dx) || !Number.isFinite(dz) || (dx === 0 && dz === 0)) return 0;
  const r = mapRotation(camYaw);
  const c = Math.cos(r);
  const s = Math.sin(r);
  // The map-space offset (dx right, dz down) put through the canvas rotation...
  const sx = dx * c - dz * s;
  const sy = dx * s + dz * c;
  // ...and read against screen-up, which is -y.
  return Math.atan2(sx, -sy);
}

/**
 * Scale that fits a whole extent inside `w x h` with a px margin.
 * @param {{minX:number,minZ:number,maxX:number,maxZ:number}} ext
 * @param {number} w @param {number} h @param {number} [margin=0]
 * @returns {number}
 */
export function fitScale(ext, w, h, margin = 0) {
  const ew = Math.max(1e-3, ext.maxX - ext.minX);
  const eh = Math.max(1e-3, ext.maxZ - ext.minZ);
  return Math.min((w - margin * 2) / ew, (h - margin * 2) / eh);
}

/**
 * Blits the whole base bitmap so that `view.cx/cz` lands at the canvas centre
 * at `view.scale`. ONE `drawImage`; the canvas clip does the windowing, which
 * is both faster and free of the source-rect edge cases a manual crop needs.
 *
 * That WHOLE is also why rotation needs nothing here: a source-rect crop sized
 * to the window would leave empty wedges sweeping in at the corners as the map
 * turned, and the fix would be to crop `ROT_OVERSCAN` wider. Blitting the
 * entire bitmap and letting the clip do the work has no corner to gap.
 *
 * @param {CanvasRenderingContext2D} ctx Already translated/clipped/rotated by
 *   the caller.
 * @param {MapBase} base
 * @param {MapView} view
 */
export function blitBase(ctx, base, view) {
  const k = view.scale / base.scale;
  const dw = base.w * k;
  const dh = base.h * k;
  const dx = viewX(view, base.minX);
  const dy = viewY(view, base.minZ);
  ctx.drawImage(base.canvas, dx, dy, dw, dh);
}

// ─────────────────────────────────────────────────────────────────────────────
// Overlay drawing
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Dark grid stand-in for "this scene has no `level.map`" (every dev scene, and
 * the city until the island lands). Deliberately looks deliberate — an empty
 * black square reads as a broken HUD.
 *
 * It fills EXACTLY `w x h` from (0,0), so a rotated caller must hand it the
 * overscanned rect and translate to its corner (see `Minimap.jsx`), or the
 * window corners go bare. `offsetX/offsetY` are a phase, not a position: they
 * are what makes the grid scroll with the player, and under rotation they keep
 * doing that unchanged, because inside a rotated context the axes ARE the world
 * axes — the caller only has to fold the overscan pad into the phase.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} w @param {number} h
 * @param {{spacing?: number, offsetX?: number, offsetY?: number}} [o]
 */
export function drawPlaceholder(ctx, w, h, o = {}) {
  const { spacing = 22, offsetX = 0, offsetY = 0 } = o;
  ctx.fillStyle = MAP_COLORS.ocean;
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = MAP_COLORS.grid;
  ctx.lineWidth = 1;
  ctx.beginPath();
  const ox = ((offsetX % spacing) + spacing) % spacing;
  const oy = ((offsetY % spacing) + spacing) % spacing;
  for (let x = ox; x < w; x += spacing) {
    ctx.moveTo(Math.round(x) + 0.5, 0);
    ctx.lineTo(Math.round(x) + 0.5, h);
  }
  for (let y = oy; y < h; y += spacing) {
    ctx.moveTo(0, Math.round(y) + 0.5);
    ctx.lineTo(w, Math.round(y) + 0.5);
  }
  ctx.stroke();
}

/**
 * The player: a white chevron with a dark keyline so it survives on a pale
 * road as well as on dark water. `yaw` is the engine heading convention — see
 * the file header for why the rotation is `PI - yaw`.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} px @param {number} py Canvas position, CSS px.
 * @param {number} yaw Radians, `atan2(dx, dz)`.
 * @param {number} [size=9] Chevron height in px.
 */
export function drawPlayerMarker(ctx, px, py, yaw, size = 9) {
  const s = size;
  ctx.save();
  ctx.translate(px, py);
  ctx.rotate(Math.PI - (Number.isFinite(yaw) ? yaw : 0));
  ctx.beginPath();
  ctx.moveTo(0, -s * 0.62);
  ctx.lineTo(s * 0.46, s * 0.5);
  ctx.lineTo(0, s * 0.22);
  ctx.lineTo(-s * 0.46, s * 0.5);
  ctx.closePath();
  ctx.fillStyle = MAP_COLORS.player;
  ctx.fill();
  ctx.strokeStyle = MAP_COLORS.playerEdge;
  ctx.lineWidth = 1.25;
  ctx.lineJoin = 'round';
  ctx.stroke();
  ctx.restore();
}

/**
 * One blip: a filled dot with a dark keyline, same reasoning as the chevron.
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} px @param {number} py
 * @param {string} kind One of {@link BLIP_COLORS}' keys.
 * @param {number} [r=3]
 */
export function drawBlip(ctx, px, py, kind, r = 3) {
  ctx.beginPath();
  ctx.arc(px, py, r, 0, Math.PI * 2);
  ctx.fillStyle = BLIP_COLORS[kind] ?? BLIP_COLORS.civilian;
  ctx.fill();
  ctx.strokeStyle = 'rgba(6,8,12,0.85)';
  ctx.lineWidth = 1;
  ctx.stroke();
}

// ─────────────────────────────────────────────────────────────────────────────
// Mission markers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ONE mission marker: a hollow ring with a dark keyline, optionally carrying a
 * one-or-two character label. Hollow on purpose — a filled disc at this size is
 * indistinguishable from a blip, and the whole point of a marker is that it is
 * NOT a person.
 *
 * ⚠ It is drawn in MAP SPACE like everything else, so under the minimap's
 * rotation the ring comes out right for free (a circle is rotation-invariant)
 * — but the LABEL does not. The label is therefore drawn by
 * {@link drawMarkerLabel} OUTSIDE the rotated context; see `Minimap.jsx`.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} px @param {number} py
 * @param {Object} mk A marker record: `{kind, tracked, dim}`.
 * @param {number} [r=5] Glyph radius in px — NOT the world radius. A mission
 *   ring is 3 m across and a `goto` radius can be 16; drawing them to scale
 *   would make one invisible and the other a blob.
 */
export function drawMissionMarker(ctx, px, py, mk, r = 5) {
  const colour = MARKER_COLORS[mk?.kind] ?? MARKER_COLORS.objective;
  const alpha = mk?.dim ? 0.42 : 1;

  ctx.save();
  ctx.globalAlpha = alpha;

  // The tracked marker gets a soft outer halo, which is what makes ONE of
  // several read as "this one" without a second colour.
  if (mk?.tracked) {
    ctx.beginPath();
    ctx.arc(px, py, r + 3.2, 0, Math.PI * 2);
    ctx.fillStyle = colour;
    ctx.globalAlpha = alpha * 0.22;
    ctx.fill();
    ctx.globalAlpha = alpha;
  }

  // Dark keyline first, so the ring survives on a pale road as well as on water
  // — the same reasoning as the player chevron's.
  ctx.beginPath();
  ctx.arc(px, py, r, 0, Math.PI * 2);
  ctx.lineWidth = 3.4;
  ctx.strokeStyle = 'rgba(6,8,12,0.85)';
  ctx.stroke();
  ctx.lineWidth = 1.9;
  ctx.strokeStyle = colour;
  ctx.stroke();

  ctx.restore();
}

/**
 * A marker's LABEL, drawn upright. Split from {@link drawMissionMarker} because
 * text inside a rotated context is text upside down half the time — the same
 * rule the minimap's north badge follows.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} px @param {number} py FINAL screen px (post-rotation — use
 *   {@link viewPoint}, not {@link viewX}).
 * @param {Object} mk
 * @param {number} [size=9] Font px.
 */
export function drawMarkerLabel(ctx, px, py, mk, size = 9) {
  const text = mk?.label;
  if (!text) return;
  ctx.save();
  ctx.globalAlpha = mk?.dim ? 0.42 : 1;
  ctx.font = `700 ${size}px "Barlow Condensed", system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = 2.6;
  ctx.strokeStyle = 'rgba(6,8,12,0.9)';
  ctx.strokeText(text, px, py);
  ctx.fillStyle = MARKER_COLORS[mk?.kind] ?? MARKER_COLORS.objective;
  ctx.fillText(text, px, py);
  ctx.restore();
}

/**
 * CLAMPS AN OFF-WINDOW POINT TO THE EDGE, so a marker the player cannot see
 * still says which way it is. GTA's minimap does exactly this and it is the
 * single most useful thing on the widget: without it a mission 200 m away is
 * simply absent, and "absent" and "you have arrived" look identical.
 *
 * The clamp is to the INSCRIBED CIRCLE rather than to the rounded rect, for the
 * same reason the north badge orbits it: a point walked round the rect's
 * perimeter jumps in speed at the corners and the arrow visibly stutters as you
 * turn, where a circle is smooth all the way round.
 *
 * ⚠ It takes FINAL SCREEN px (post-rotation), not map space — an edge is a
 * property of the window, and the window does not rotate.
 *
 * @param {number} px @param {number} py Final screen px.
 * @param {number} w @param {number} h Canvas size, CSS px.
 * @param {number} pad Distance in from the edge, px.
 * @param {{x: number, y: number, edge: boolean, dir: number}} out REUSED.
 * @returns {{x: number, y: number, edge: boolean, dir: number}} `out`. `edge` is
 *   true when the point was actually outside; `dir` is the screen-space bearing
 *   from the centre, for an arrow head.
 */
export function clampToEdge(px, py, w, h, pad, out) {
  const cx = w * 0.5;
  const cy = h * 0.5;
  const dx = px - cx;
  const dy = py - cy;
  const d = Math.hypot(dx, dy);
  const rim = Math.min(w, h) * 0.5 - pad;
  out.dir = Math.atan2(dy, dx);
  if (d <= rim || d < 1e-6) {
    out.x = px;
    out.y = py;
    out.edge = false;
    return out;
  }
  out.x = cx + (dx / d) * rim;
  out.y = cy + (dy / d) * rim;
  out.edge = true;
  return out;
}

/**
 * The little triangle that says "it is this way", drawn just outside a clamped
 * marker. Only ever used with {@link clampToEdge}'s `edge: true`.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} px @param {number} py @param {number} dir Radians, screen space.
 * @param {string} colour
 * @param {number} [size=4]
 */
export function drawEdgeArrow(ctx, px, py, dir, colour, size = 4) {
  ctx.save();
  ctx.translate(px, py);
  ctx.rotate(dir);
  ctx.beginPath();
  ctx.moveTo(size + 5, 0);
  ctx.lineTo(size - 1, -size * 0.9);
  ctx.lineTo(size - 1, size * 0.9);
  ctx.closePath();
  ctx.fillStyle = colour;
  ctx.strokeStyle = 'rgba(6,8,12,0.85)';
  ctx.lineWidth = 1;
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

/**
 * Rounded-rect path (no `ctx.roundRect` — Safari shipped it late and the HUD
 * must not depend on it).
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x @param {number} y @param {number} w @param {number} h
 * @param {number} r
 */
export function roundRectPath(ctx, x, y, w, h, r) {
  const rad = Math.min(r, w * 0.5, h * 0.5);
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.lineTo(x + w - rad, y);
  ctx.arcTo(x + w, y, x + w, y + rad, rad);
  ctx.lineTo(x + w, y + h - rad);
  ctx.arcTo(x + w, y + h, x + w - rad, y + h, rad);
  ctx.lineTo(x + rad, y + h);
  ctx.arcTo(x, y + h, x, y + h - rad, rad);
  ctx.lineTo(x, y + rad);
  ctx.arcTo(x, y, x + rad, y, rad);
  ctx.closePath();
}

// ─────────────────────────────────────────────────────────────────────────────
// The south-west villages
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The published village disc, drawn as a ring plus its name — FULL MAP ONLY.
 *
 * `level.map.villages` is `{x, z, r, name}` per settlement
 * (`levels/city/rural.js`; `docs/islands.md` states that the disc IS the
 * contract — do not re-derive a village from its adobe instances and do not
 * re-sort the list). Everything here reads that record and nothing else.
 *
 * ⚠ **Drawn TO SCALE, unlike a mission marker.** A marker takes a fixed glyph
 * radius because a 3 m ring and a 16 m `goto` circle mean the same thing to the
 * player; a village is a PLACE with a real extent, and a ring that did not
 * match the ground would be pointing at the wrong amount of desert. At the
 * shipped `r` 36.98 m and the full map's fitted scale (~0.35 px/m on a 1440 px
 * window) that is a ~13 px ring.
 *
 * ⚠ **The minimap deliberately does not call this**, for the two reasons
 * {@link districtLabels} gives: text is unreadable at 220 px, and at the
 * minimap's 1.4 px/m the same disc is a 104 px ring — a third of the widget,
 * drawn round a place the player is usually nowhere near. It also takes
 * {@link viewX}/{@link viewY} rather than {@link viewPoint}, so it belongs
 * OUTSIDE any rotation (the `drawMarkerLabel` rule: text in a rotated context
 * is text upside down half the time).
 *
 * Empty, absent or malformed lists draw nothing — every dev scene and both
 * galleries have no villages at all.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {any} map `level.map`.
 * @param {MapView} v
 * @param {number} [minR=6] Floor on the ring radius in px, so a village cannot
 *   vanish into its own keyline on a small window — the {@link ROAD_MIN_PX}
 *   idea, applied to the one other thing on this map with a world size.
 */
export function drawVillages(ctx, map, v, minR = 6) {
  const list = map?.villages;
  if (!Array.isArray(list) || !list.length) return;

  ctx.save();
  ctx.font = '700 10px "Barlow Condensed", system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';

  for (const village of list) {
    if (!village || !Number.isFinite(village.x) || !Number.isFinite(village.z)) continue;
    const px = viewX(v, village.x);
    const py = viewY(v, village.z);
    const r = Math.max(minR, (Number.isFinite(village.r) ? village.r : 0) * v.scale);

    // Dark keyline under the ring, exactly like the mission marker and the
    // player chevron: this lands on open country, on a dirt road and on a
    // trunk road, and it has to survive all three.
    ctx.beginPath();
    ctx.arc(px, py, r, 0, Math.PI * 2);
    ctx.lineWidth = 2.6;
    ctx.strokeStyle = 'rgba(6,8,12,0.75)';
    ctx.stroke();
    ctx.lineWidth = 1.2;
    ctx.strokeStyle = MAP_COLORS.villageEdge;
    ctx.stroke();

    // A faint wash inside it. HOLLOW would read as a mission ring; filled
    // solid would read as a block. This reads as ground that is spoken for.
    ctx.globalAlpha = 0.16;
    ctx.fillStyle = MAP_COLORS.villageLabel;
    ctx.fill();
    ctx.globalAlpha = 1;

    const name = typeof village.name === 'string' ? village.name.trim() : '';
    if (!name) continue;
    const ty = py + r + 7;
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(5,7,11,0.9)';
    ctx.strokeText(name.toUpperCase(), px, ty);
    ctx.fillStyle = MAP_COLORS.villageLabel;
    ctx.fillText(name.toUpperCase(), px, ty);
  }

  ctx.restore();
}

/** @type {WeakMap<object, Array<{x:number,z:number,zone:string,area:number}>>} */
const labelCache = new WeakMap();

/**
 * District labels for the FULL map only — the minimap is far too small for
 * text. One label per district centre, biggest first, so the caller can drop
 * the ones that would collide (see `PauseMenu.jsx`); the list is cached per
 * map object because it is pure derivation.
 *
 * @param {any} map
 * @returns {Array<{x:number,z:number,zone:string,area:number}>}
 */
export function districtLabels(map) {
  if (!map || typeof map !== 'object') return [];
  const hit = labelCache.get(map);
  if (hit) return hit;

  /** @type {Array<{x:number,z:number,zone:string,area:number}>} */
  const out = [];
  const r = { minX: 0, minZ: 0, maxX: 0, maxZ: 0 };
  for (const d of map.districts ?? []) {
    const zone = d?.zone;
    if (!zone || !ZONE_LABELS[zone]) continue;
    if (!rectBounds(d?.rect ?? d, r)) continue;
    out.push({
      x: (r.minX + r.maxX) * 0.5,
      z: (r.minZ + r.maxZ) * 0.5,
      zone,
      area: (r.maxX - r.minX) * (r.maxZ - r.minZ),
    });
  }
  out.sort((a, b) => b.area - a.area);
  labelCache.set(map, out);
  return out;
}
